import { useState, useEffect } from "react";
import { LuCheck } from "react-icons/lu";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { put } from "src/services/HttpClient/HttpClient";

export default function Pricing() {
    const [items, setItems] = useState([]);
    const [livePrices, setLivePrices] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // دریافت هم‌زمان اطلاعات تغییرات و قیمت‌های لحظه‌ای
                const [changesRes, pricesRes] = await Promise.all([
                    fetch("https://api.ksgold.ir/api/changes/"),
                    fetch("https://api.ksgold.ir/api/prices/?format=json")
                ]);

                if (!changesRes.ok || !pricesRes.ok) throw new Error("خطا در دریافت اطلاعات");

                const changesData = await changesRes.json();
                const pricesData = await pricesRes.json();

                setItems(changesData);
                setLivePrices(pricesData);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) return <div className="text-center py-8">در حال بارگذاری...</div>;

    return (
        <div className="max-w-[1100px] w-full mx-auto p-4" dir="rtl">
            <div className="grid grid-cols-5 gap-4 font-bold text-center mb-4 bg-gray-100 p-3 rounded-xl text-gray-700">
                <span>عنوان</span>
                <span>خرید عادی</span>
                <span>خرید همکار</span>
                <span>فروش عادی</span>
                <span>فروش همکار</span>
            </div>
            <div className="flex flex-col gap-4">
                {items.map((item) => (
                    <PriceEditRow 
                        key={item.Symbol} 
                        itemData={item} 
                        liveItem={livePrices ? livePrices[item.Symbol] : null} 
                    />
                ))}
            </div>
        </div>
    );
}

const PriceEditRow = ({ itemData, liveItem }) => {
    const symbol = itemData.Symbol;
    const displayName = itemData.Name || symbol;

    const [prices, setPrices] = useState({
        buy_amount_normal: itemData.BuyNormal ?? 0,
        buy_amount_colleague: itemData.BuyColleague ?? 0,
        sell_amount_normal: itemData.SellNormal ?? 0,
        sell_amount_colleague: itemData.SellColleague ?? 0,
    });

    const [initialPrices, setInitialPrices] = useState({ ...prices });
    const [loadingField, setLoadingField] = useState("");

    const handleChange = (field, value) => {
        setPrices(prev => ({ ...prev, [field]: value }));
    };

    const updatePrice = async (field) => {
        try {
            setLoadingField(field);
            const payload = { 
                symbol, 
                [field]: Number(prices[field]) || 0 
            };

            await put("/api/update/", payload);
            setInitialPrices(prev => ({ ...prev, [field]: prices[field] }));
        } catch (error) {
            console.error(error);
            alert("❌ خطا در آپدیت قیمت");
        } finally {
            setLoadingField("");
        }
    };

    // متد فرمت‌دهی اعداد به فارسی و سه رقم سه رقم
    const formatNumber = (num) => {
        if (num === undefined || num === null || isNaN(num) || num === 0) return '-';
        return Number(num).toLocaleString('fa-IR');
    };

    // استخراج قیمت‌های زنده بر اساس ساختار کلیدهای API قیمت‌ها
    const getLiveValue = (keyType) => {
        if (!liveItem) return null;
        switch (keyType) {
            case "buy_amount_normal":
                return liveItem.BuyPriceNormal ?? liveItem[`${symbol}_BUY_NORMAL`];
            case "buy_amount_colleague":
                return liveItem.BuyPriceColleague ?? liveItem[`${symbol}_BUY_COLLEAGUE`];
            case "sell_amount_normal":
                return liveItem.SellPriceNormal ?? liveItem[`${symbol}_SELL_NORMAL`];
            case "sell_amount_colleague":
                return liveItem.SellPriceColleague ?? liveItem[`${symbol}_SELL_COLLEAGUE`];
            default:
                return null;
        }
    };

    const fields = [
        { key: "buy_amount_normal", label: "خرید عادی" },
        { key: "buy_amount_colleague", label: "خرید همکار" },
        { key: "sell_amount_normal", label: "فروش عادی" },
        { key: "sell_amount_colleague", label: "فروش همکار" },
    ];

    return (
        <div className="grid grid-cols-5 gap-4 items-center bg-white p-3 rounded-2xl shadow-sm border border-gray-100">
            <div className="font-semibold text-gray-800 text-center truncate" title={displayName}>
                {displayName}
            </div>

            {fields.map(({ key }) => {
                const isChanged = String(prices[key]) !== String(initialPrices[key]);
                const isLoading = loadingField === key;
                const liveValue = getLiveValue(key);

                return (
                    <div key={key} className="flex flex-col gap-1">
                        <div className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2 border border-gray-200">
                            <input
                                type="number"
                                value={prices[key]}
                                onChange={(e) => handleChange(key, e.target.value)}
                                className="bg-transparent outline-none w-full text-center text-sm"
                            />
                            {isChanged && (
                                <button onClick={() => updatePrice(key)} disabled={isLoading} className="mr-1 flex-shrink-0">
                                    {isLoading ? (
                                        <AiOutlineLoading3Quarters className="animate-spin text-gray-500" size={18} />
                                    ) : (
                                        <LuCheck className="text-green-600 cursor-pointer hover:scale-110 transition-transform" size={18} />
                                    )}
                                </button>
                            )}
                        </div>

                        {/* نمایش قیمت زنده زیر اینپوت */}
                        <div className="text-[11px] text-gray-500 text-center font-medium">
                            قیمت لحظه‌ای: <span className="text-blue-600 font-semibold">{formatNumber(liveValue)}</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};